import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, {
  formatApiError, SCENARIO_ORIGIN, SCENARIO_STATUS, SCENARIO_STATUS_KEYS,
  tScenarioOrigin, tScenarioStatus, tEventType, tSimState,
  SCENARIO_EVENT_TYPE, SCENARIO_EVENT_KEYS, SIMULATED_STATE, SIMULATED_STATE_KEYS,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { GitBranch, Plus, ArrowRight, Loader2, X, Trash2 } from "lucide-react";

function StatusBadge({ value }) {
  const c = SCENARIO_STATUS[value] || SCENARIO_STATUS.draft;
  return <Badge variant="outline" className={`rounded-sm text-xs font-mono ${c.cls}`} data-testid={`scenario-status-${value}`}>{tScenarioStatus(value)}</Badge>;
}

function MultiPick({ label, options, value, onChange, testid }) {
  const add = (v) => { if (v && !value.includes(v)) onChange([...value, v]); };
  const remove = (v) => onChange(value.filter((x) => x !== v));
  const labelOf = (v) => options.find((o) => o.value === v)?.label || v;
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Select value="" onValueChange={add}>
        <SelectTrigger className="mt-1 rounded-sm" data-testid={testid}><SelectValue placeholder="Ajouter…" /></SelectTrigger>
        <SelectContent className="max-h-64">
          {options.filter((o) => !value.includes(o.value)).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {value.map((v) => (
          <Badge key={v} variant="outline" className="rounded-sm text-[11px] gap-1">
            {labelOf(v)}
            <button onClick={() => remove(v)} data-testid={`${testid}-remove-${v}`}><X className="h-3 w-3" /></button>
          </Badge>
        ))}
      </div>
    </div>
  );
}

const EMPTY = {
  title: "", description: "", threat_id: "", technique_external_ids: [], vulnerability_ids: [],
  initial_node_ids: [], target_service_ids: [], chain: [], status: "draft",
};

export default function Scenarios() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const qc = useQueryClient();
  const [status, setStatus] = useState("all");
  const [origin, setOrigin] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const { data = [], isLoading } = useQuery({
    queryKey: ["scenarios"], queryFn: async () => (await api.get("/scenarios")).data,
  });
  const { data: threats = [] } = useQuery({ queryKey: ["threats"], queryFn: async () => (await api.get("/threats")).data, enabled: open });
  const { data: vulns = [] } = useQuery({ queryKey: ["vulnerabilities"], queryFn: async () => (await api.get("/vulnerabilities")).data, enabled: open });
  const { data: assets = [] } = useQuery({ queryKey: ["assets"], queryFn: async () => (await api.get("/assets")).data, enabled: open });
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: async () => (await api.get("/services")).data, enabled: open });
  const { data: techniques = [] } = useQuery({ queryKey: ["mitre-techniques"], queryFn: async () => (await api.get("/mitre/techniques")).data, enabled: open });

  const create = useMutation({
    mutationFn: async () => (await api.post("/scenarios", form)).data,
    onSuccess: (s) => { toast.success("Scénario créé"); setOpen(false); setForm(EMPTY); qc.invalidateQueries({ queryKey: ["scenarios"] }); navigate(`/scenarios/${s.id}`); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const rows = data.filter((s) => (status === "all" || s.status === status) && (origin === "all" || s.origin === origin));
  const canWrite = can("scenario:write");

  const techOpts = techniques.map((t) => ({ value: t.external_id, label: `${t.external_id} — ${t.name}` }));
  const vulnOpts = vulns.map((v) => ({ value: v.id, label: v.title }));
  const assetOpts = assets.map((a) => ({ value: a.id, label: a.name }));
  const serviceOpts = services.map((s) => ({ value: s.id, label: s.name }));
  const stepTargetOpts = (type) => (type === "service" ? serviceOpts : assetOpts);

  const addStep = () => setForm((f) => ({ ...f, chain: [...f.chain, { order: f.chain.length + 1, event_type: "asset_compromised", target_ref: { type: "asset", id: "" }, technique_external_id: "", expected_effect: "COMPROMISED", note: "" }] }));
  const setStep = (i, patch) => setForm((f) => ({ ...f, chain: f.chain.map((s, idx) => idx === i ? { ...s, ...patch } : s) }));
  const removeStep = (i) => setForm((f) => ({ ...f, chain: f.chain.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, order: idx + 1 })) }));

  return (
    <AppShell title="Scénarios" subtitle="Hypothèses cyber STRUCTURÉES (déclaratives) — chaîne menace→technique→vuln→actif→service. Aucune simulation."
      actions={
        <div className="flex items-center gap-2">
          <Select value={origin} onValueChange={setOrigin}>
            <SelectTrigger className="w-36 rounded-sm" data-testid="scenario-filter-origin"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Toutes origines</SelectItem>{Object.keys(SCENARIO_ORIGIN).map((k) => <SelectItem key={k} value={k}>{tScenarioOrigin(k)}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-36 rounded-sm" data-testid="scenario-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tous statuts</SelectItem>{SCENARIO_STATUS_KEYS.map((k) => <SelectItem key={k} value={k}>{tScenarioStatus(k)}</SelectItem>)}</SelectContent>
          </Select>
          {canWrite && <Button className="rounded-sm gap-2" onClick={() => setOpen(true)} data-testid="create-scenario-button"><Plus className="h-4 w-4" /> Nouveau scénario</Button>}
        </div>
      }>
      <div className="border border-border bg-card rounded-sm overflow-hidden" data-testid="scenarios-table">
        {isLoading ? (
          <div className="p-6 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Chargement…</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                <TableHead>Titre</TableHead><TableHead>Origine</TableHead><TableHead>Statut</TableHead>
                <TableHead>Étapes</TableHead><TableHead className="text-right">Fiche</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8">Aucun scénario.</TableCell></TableRow>
              ) : rows.map((s) => (
                <TableRow key={s.id} className="border-border cursor-pointer" onClick={() => navigate(`/scenarios/${s.id}`)} data-testid={`scenario-row-${s.id}`}>
                  <TableCell className="font-medium">{s.title}</TableCell>
                  <TableCell className="text-xs">{tScenarioOrigin(s.origin)}</TableCell>
                  <TableCell><StatusBadge value={s.status} /></TableCell>
                  <TableCell className="text-xs font-mono">{(s.chain || []).length}</TableCell>
                  <TableCell className="text-right"><ArrowRight className="h-4 w-4 text-muted-foreground inline" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="scenario-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Nouveau scénario (hypothèse structurée)</DialogTitle>
            <DialogDescription>Chaîne déclarative. Toutes les références doivent exister. Aucune simulation n'est exécutée.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div><Label className="text-xs">Titre *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 rounded-sm" data-testid="scenario-title-input" /></div>
            <div><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-sm" data-testid="scenario-description-input" /></div>
            <div>
              <Label className="text-xs">Menace</Label>
              <Select value={form.threat_id} onValueChange={(v) => setForm({ ...form, threat_id: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="scenario-threat-select"><SelectValue placeholder="Aucune" /></SelectTrigger>
                <SelectContent className="max-h-64">{threats.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <MultiPick label="Techniques ATT&CK" options={techOpts} value={form.technique_external_ids} onChange={(v) => setForm({ ...form, technique_external_ids: v })} testid="scenario-techniques-pick" />
            <MultiPick label="Vulnérabilités" options={vulnOpts} value={form.vulnerability_ids} onChange={(v) => setForm({ ...form, vulnerability_ids: v })} testid="scenario-vulns-pick" />
            <MultiPick label="Points d'entrée (actifs)" options={assetOpts} value={form.initial_node_ids} onChange={(v) => setForm({ ...form, initial_node_ids: v })} testid="scenario-initial-pick" />
            <MultiPick label="Services cibles" options={serviceOpts} value={form.target_service_ids} onChange={(v) => setForm({ ...form, target_service_ids: v })} testid="scenario-targets-pick" />

            <div className="border-t border-border pt-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs">Chaîne causale (étapes ordonnées)</Label>
                <Button size="sm" variant="outline" className="rounded-sm gap-1 h-7" onClick={addStep} data-testid="add-chain-step-button"><Plus className="h-3.5 w-3.5" /> Étape</Button>
              </div>
              <div className="space-y-3">
                {form.chain.map((st, i) => (
                  <div key={i} className="border border-border rounded-sm p-3 space-y-2" data-testid={`chain-step-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-mono text-muted-foreground">Étape {st.order}</span>
                      <button onClick={() => removeStep(i)} data-testid={`chain-step-${i}-remove`}><Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" /></button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Select value={st.event_type} onValueChange={(v) => setStep(i, { event_type: v })}>
                        <SelectTrigger className="rounded-sm h-8 text-xs" data-testid={`chain-step-${i}-event`}><SelectValue /></SelectTrigger>
                        <SelectContent>{SCENARIO_EVENT_KEYS.map((k) => <SelectItem key={k} value={k}>{SCENARIO_EVENT_TYPE[k]}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={st.expected_effect} onValueChange={(v) => setStep(i, { expected_effect: v })}>
                        <SelectTrigger className="rounded-sm h-8 text-xs" data-testid={`chain-step-${i}-effect`}><SelectValue placeholder="État attendu" /></SelectTrigger>
                        <SelectContent>{SIMULATED_STATE_KEYS.map((k) => <SelectItem key={k} value={k}>{SIMULATED_STATE[k]}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={st.target_ref.type} onValueChange={(v) => setStep(i, { target_ref: { type: v, id: "" } })}>
                        <SelectTrigger className="rounded-sm h-8 text-xs" data-testid={`chain-step-${i}-target-type`}><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="asset">Actif</SelectItem><SelectItem value="service">Service</SelectItem></SelectContent>
                      </Select>
                      <Select value={st.target_ref.id} onValueChange={(v) => setStep(i, { target_ref: { ...st.target_ref, id: v } })}>
                        <SelectTrigger className="rounded-sm h-8 text-xs" data-testid={`chain-step-${i}-target-id`}><SelectValue placeholder="Cible" /></SelectTrigger>
                        <SelectContent className="max-h-56">{stepTargetOpts(st.target_ref.type).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
                {form.chain.length === 0 && <p className="text-xs text-muted-foreground">Ajoutez au moins une étape pour structurer la chaîne.</p>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Annuler</Button>
            <Button className="rounded-sm gap-2" disabled={!form.title || create.isPending} onClick={() => create.mutate()} data-testid="save-scenario-button">
              {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
